
























import { Vue, Component, Prop } from 'vue-property-decorator';
import { Bar } from 'vue-chartjs/legacy';
import zoomPlugin from 'chartjs-plugin-zoom';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ZoomOutIcon } from 'vue-feather-icons';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  LegendItem,
  ChartEvent,
} from 'chart.js';

ChartJS.register(
  ChartDataLabels,
  zoomPlugin,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
);

@Component({
  components: {
    Bar,
    ZoomOutIcon,
  },
})
export default class StackedBarLineChart extends Vue {
  @Prop({ type: String, required: true }) title!: string;
  @Prop({ type: String, required: true }) subtitle!: string;
  @Prop({ type: Number, default: 400 }) height!: number;
  // Das komplette Chart-Datenobjekt als Prop
  @Prop({ type: Object, required: true }) chartData!: any;
  @Prop({ type: String, required: false }) dataLabel!: string;

  resetZoomChart() {
    const chart = this.getChartInstance();
    if (chart && typeof chart.resetZoom === 'function') {
      chart.resetZoom();
    }
  }

  getChartInstance() {
    if (!this.$refs.stackedBarLineChart) return null;
    const barComponent = this.$refs.stackedBarLineChart as any;
    if (barComponent.getCurrentChart && typeof barComponent.getCurrentChart === 'function') {
      return barComponent.getCurrentChart();
    }
    return null;
  }

  // Die Methode als Teil der Klasse definieren
  updateQuantityLine(chart: any) {
    // Finde die Anzahl-Linie
    const quantityLineIndex = chart.data.datasets.findIndex((ds: any) => ds.label === 'Anzahl');
    if (quantityLineIndex === -1) return;

    const quantityLine = chart.data.datasets[quantityLineIndex];

    // Für jeden Datenpunkt...
    for (let i = 0; i < chart.data.labels.length; i++) {
      let totalQuantity = 0;

      // Durchlaufe alle Datasets außer der Anzahl-Linie
      chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
        if (dataset.label === 'Anzahl') return;

        // Wenn das Dataset sichtbar ist, füge seine Quantity hinzu
        if (!chart.getDatasetMeta(datasetIndex).hidden) {
          totalQuantity += dataset.data[i].quantity || 0;
        }
      });

      // Aktualisiere den Quantity-Wert in der Anzahl-Linie
      quantityLine.data[i].quantity = totalQuantity;
    }
  }

  handleLegendClick(e: MouseEvent, legendItem: LegendItem, legend: any) {
    const index = legendItem.datasetIndex;
    if (index === undefined) return; // Sicherheitsprüfung hinzugefügt

    const chart = legend.chart;

    // Standard-Verhalten: Dataset ein-/ausblenden
    const meta = chart.getDatasetMeta(index);
    meta.hidden = meta.hidden === null ? !chart.data.datasets[index]?.hidden : null;

    // Wenn es sich nicht um die Anzahl-Linie handelt, aktualisiere die Quantities
    if (chart.data.datasets[index]?.label !== 'Anzahl') {
      this.updateQuantityLine(chart);
    }

    chart.update();
  }

  chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        grid: { display: true },
        title: { display: false, text: this.dataLabel },
        ticks: {
          callback: function (value: number) {
            return value >= 1000 ? value / 1000 + 'k' : value;
          },
        },
      },
      y1: {
        position: 'right',
        grid: { display: false },
        beginAtZero: true,
        title: { display: true, text: 'Anzahl' },
        afterDataLimits: (scale: any) => {
          scale.max = scale.max + 1.5;
        },
      },
    },
    plugins: {
      datalabels: {
        display: function (context: any) {
          return context.dataset.label === 'Anzahl';
        },
        formatter: function (value: any) {
          // Bei der Anzahl-Linie zeigen wir den quantity-Wert an
          if (value && typeof value === 'object' && 'quantity' in value) {
            return value.quantity;
          }
          return '';
        },
        color: '#fff',
        font: {
          weight: 'normal',
          size: 11,
        },
        backgroundColor: '#3A7BC7',
        padding: { top: 1, bottom: 1, left: 4, right: 4 },
        align: 'center',
        anchor: 'center',
        clip: true,
      },
      legend: {
        onClick: function (
          this: StackedBarLineChart,
          e: MouseEvent,
          legendItem: LegendItem,
          legend: any,
        ) {
          this.handleLegendClick(e, legendItem, legend);
        },
        labels: {
          boxWidth: 15,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)',
        boxPadding: 5,
        padding: 8,
      },
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: true,
            backgroundColor: 'rgb(58, 123, 199,0.2)',
            borderColor: 'rgb(58, 123, 199)',
            borderWidth: 1,
          },
          pinch: { enabled: true },
        },
      },
    },
  };

  mounted() {
    this.$nextTick(() => {
      const chart = this.getChartInstance();
      if (chart) {
        // Setze den z-Index der Linie höher
        const quantityLineIndex = chart.data.datasets.findIndex((ds: any) => ds.label === 'Anzahl');
        if (quantityLineIndex !== -1) {
          chart.data.datasets[quantityLineIndex].order = -1;
        }

        chart.options.plugins.legend.onClick = this.handleLegendClick.bind(this);
        chart.update();
      }
    });
  }
}
